import { pipe, toPairs, map, fromPairs, anyPass, isEmpty, isNil, allPass, is, values, all, when, always, ifElse, replace, toLower, split, last, includes, __, addIndex, takeLast, sum, both, lte, gte, take, splitEvery, splitAt, equals, modulo, zipWith, multiply, gt, subtract, toUpper } from 'ramda';
export { assoc, dissocPath, equals, ifElse, isEmpty, pathSatisfies, pipe, pluck } from 'ramda';
import { addDays, getYear, getDaysInMonth, differenceInYears } from 'date-fns';
export { addBusinessDays, addDays, addHours, addMonths, endOfDay, endOfMonth, format, formatISO, isAfter, isBefore, isSameDay, isSameMonth, isValid, isWeekend, isWithinInterval, parseISO, startOfDay, startOfISOWeek, startOfMonth, subDays, subMonths } from 'date-fns';
import { throwError } from 'rxjs';

/**
 * Updates the values of every property in an object using the provided function.
 */
const evolveAny = (fn) => pipe(toPairs, map(([key, value]) => [key, fn(value)]), fromPairs);

/**
 * Returns `true` if the value is an empty object, empty array, empty string, undefined or null.
 */
const isNullable = anyPass([isEmpty, isNil]);
/**
 * Returns `true` if all values of an object or array are nullable.
 */
const isNullableObject = allPass([is(Object), pipe(values, all(isNullable))]);
/**
 * Updates all nullable values in an object to `null`.
 *
 * **Input**
 *
 * ```
 * {
 *   aaa: undefined,
 *   bbb: "",
 *   ccc: {},
 *   ddd: [],
 *   eee: null,
 *   fff: {
 *     ggg: ""
 *   },
 *   hhh: 111
 * }
 * ```
 * **Output**
 *
 * ```
 * {
 *   aaa: null,
 *   bbb: null,
 *   ccc: null,
 *   ddd: null,
 *   eee: null,
 *   fff: null,
 *   hhh: 111
 * }
 * ```
 */
const nullify = (value) => pipe(when(isNullable, always(null)), when(is(Object), ifElse(is(Array), map(nullify), evolveAny(nullify))), when(isNullableObject, always(null)))(value);

// TODO(stefan): TSLint does not work well with Ramda, although this is valid syntax it will still throw linting errors.
// Not sure how to solve this is, so will ignore those errors for now.
/**
 * Recursively removes illegal characters from string-value properties of an object
 */
const sanitize = (value) => pipe(
// @ts-ignore
when(is(String), replace(/[^a-zA-ZåäöÅÄÖÆæØøÜüßÉéÈèÊêËëÇç0-9-+@$._\ \,\|\/\(\)]/gi, "")), 
// @ts-ignore
when(is(Object), evolveAny(sanitize))
// @ts-ignore
)(value);

/**
 * Coerces a data-bound value (typically a string) to a boolean.
 *
 * This is usefull when declaring component inputs as standard HTML properties such as `disabled`,
 * where `<input disabled>` should resolve to `true` and not `undefined`, and for example `<input disabled="false">`
 * should resolve to `false` and not `"false"`.
 */
const coerceBooleanProperty = (value) => {
    return value != null && `${value}` !== "false";
};

/**
 * Coerces a data-bound value (typically a string) to a number.
 */
const coerceNumberProperty = (value) => {
    if (!isNaN(parseFloat(value)) && !isNaN(Number(value)))
        return Number(value);
    else
        return NaN;
};

/**
 * Convert a kebab-cased string to camelCased.
 */
function kebabToCamelCase(input) {
    return input.replace(/-([a-z\d])/g, (_, char) => char.toUpperCase());
}
/**
 * Convert a camelCased string to kebab-cased.
 */
function camelToDashCase(input) {
    return input.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
}

/**
 * Gets the next weekday, the following monday if the provided date is a weekend or the next day after a weekday
 */
function nextWeekday(date) {
    const tomorrow = addDays(date, 1);
    return tomorrow.getDay() % 6 ? tomorrow : nextWeekday(tomorrow);
}
/**
 * Converts a date to seconds since Epoch time
 */
function toEpochSeconds(date) {
    return Math.floor(date.getTime() / 1000);
}
/**
 * Converts a seconds since Epoch time to date
 */
function toDate(epochSeconds) {
    return new Date(epochSeconds * 1000);
}
/**
 * Converts a date to string in finnish format DD.MM.YYYY
 */
function toDDMMYYYY(date, seperator = ".") {
    return (date.getDate().toString().padStart(2, "0") +
        seperator +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        seperator +
        date.getFullYear());
}
/**
 * Converts a date to string in swedish format YYYY-MM-DD
 */
function toYYYYMMDD(date, seperator = "-") {
    return (date.getFullYear() +
        seperator +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        seperator +
        date.getDate().toString().padStart(2, "0"));
}

/**
 * TODO:
 *
 *  Add validator for:
 *  - Finnish premise ID
 *
 */
/**
 * Regular expression that matches valid swedish emails.
 *
 * this regex matches swedish emails as implemented by the Orderhandler API, where
 * - `john.doe` is a unlimited number of lowercase letters and numericals, with a underscore, dash or dot as accepted special characters
 * - `@` seperator for between the first and second sections
 * - `corp.example` is the host and subdomain represented as a unlimited number of lowercase letters and numericals, with a dash as accepted special character
 * - `.com` the domain extention with a minimum of 2 and maximum of 4 lowercase letters
 * - `..` consecutive periods are exluded and will return false
 */
const SWEDISH_EMAIL = /^[a-z0-9_-]+(\.[a-z0-9_-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,4})$/;
/**
 * Regular expression that matches valid finnish emails.
 *
 * this regex matches finnish emails as implemented by backend services, where
 * - `jöhn.doe` is a unlimited number of lowercase letters (including å, ä and ö) and numericals, with a underscore, dash or dot as accepted special characters
 * - `@` seperator for between the first and second sections
 * - `corp.example` is the host and subdomain represented as a unlimited number of lowercase letters (including å, ä and ö) and numericals, with a dash as accepted special character
 * - `.com` the domain extention with a unlimited letters (including å, ä and ö) and numericals
 */
const FINNISH_EMAIL = /^[a-ö0-9][a-ö0-9|]*([a-ö0-9|._-]+)*([.][a-ö0-9]+([_][a-ö0-9]+)*)*@[a-ö0-9][a-ö0-9|]*.([a-ö0-9]*(.[a-ö0-9]*)?)$/;
/**
 * Regular expression that matches valid finnish business id.
 *
 * this regex matches finnish business id of format `2108804-8`, where
 * - `2108804` is a 7 digit business number generated by national board of taxation following 11 mod 2 algorithm.
 * - `-` seperator for number and checksum digit
 * - `8` is checksum digit
 */
const FINNISH_ORGANISATION_NUMBER_REGEXP = /^(?!0000000-0).*(\d{7}\-\d{1}?)$/;
/**
 * Regular expression that matches valid swedish organisation number.
 *
 * this regex matches swedish organisation of format `556036-2138`, where
 * - `556036213` first 9 digit except dash (`-`) follows luhn algorithm to generated checsum digit 8.
 * - `-` seperator for between 6 & 4 digit
 * - `8` is checksum digit
 */
const SWEDISH_ORGANISATION_NUMBER_REGEXP = /^(?!000000-0000)(\d{6}\-\d{4}?)$/;
/**
 * Regular expression that matches valid finnish personal number.
 *
 * this regex matches finnish personal of format `051163-050Y`or `211200A598H`, where
 * - `051163` or `211200` first 6 digit represent valid date of format `ddMMyy`
 * - `-`, `+` or `A`  represent century year like + (`1800`), - (`1900`)  and A (`2000`)
 * - `051163050` or `211200598` this combined digit except alphabet and dash (`-`) is divide by 31
 * to generate control character
 * - `Y` or `H` is control character
 */
const FINNISH_PERSONAL_NUMBER_REGEXP = /^([0][1-9]|[1|2][0-9]|[3][0|1])([0][1-9]|[1][0-2])([0-9]{2})([-+A])([0-9]{3})([A-Z0-9])$/;
/**
 * Regular expression that matches valid name.
 *
 * this regex matches names of format `Adam Bertilsson`, `Anja-Britt', 'Ceasar von Åberg'
 * and not leading space, numbers or special characters.
 */
const SWEDISH_PERSONAL_NAME_REGEXP = /^([a-zA-ZåäöÅÄÖÆæØøÜüßÉéÈèÊêËëÇç]){1,30}([a-zA-ZåäöÅÄÖÆæØøÜüßÉéÈèÊêËëÇç\ \-]){0,30}$/;
/**
 * Regular expression that matches valid swedish personal number.
 *
 * this regex matches swedish personal of format `19640823-3234`, where
 * - `19640823` or `640823` first 8 or 6 digit represent valid date of format `yyyyMMdd`
 * - `-` seperator between 8/6 and 4 digit
 * - `640823323` this combined digit except dash (`-`) follows luhn algorithm to generated checsum digit 4
 * - `4` is checksum character
 */
const SWEDISH_PERSONAL_NUMBER_REGEXP = /^(19|20)?([0-9]{2})([0][1-9]|[1][0-2])([0-9]{2})([-+|\s]?\d{4})$/;
/**
 * Regular expression that matches valid finnish phone number.
 *
 * this regex matches finnish phone numer of some format like `+358406361091`, where
 * - `+358` represent country code
 * - `406361091` represent remain part of phone number
 */
const FINNISH_PHONE_NUMBER_REGEXP = /^(((\+|0{2})(([1-9]{3})|[1-9]{2}[ ][1-9]))|(0)[1-9]{1})([0-9 -]{6,10})$/;
/**
 * Regular expression that matches valid swedish phone number.
 *
 * this regex matches finnish phone numer of some format like `+4620-820000`, where
 * - `+46` represent country code
 * - `20-820000` represent remain part of phone number
 */
const SWEDISH_PHONE_NUMBER_REGEXP = /^(00[1-9][1-9]|\+|0)[1-9][0-9]{0,2}[0-9\-\/]{5,8}$/;
/**
 * Regular expression that matches valid swedish pod id.
 *
 * this regex matches swedish phone podid of format like `735999100054384456`, where
 * - `735999100054384456` represent unique podid for apartment/house which follows GS-1 standard algorithm to validate
 */
const SWEDISH_PREMISE_ID_REGEXP = /^[0-9]{18}$/;
/**
 * Regular expression that matches valid finnish postal code.
 *
 * this regex matches finnish postal code of format like `62100`, where
 * - `62100` represent finnish valid postal code
 */
const FINNISH_POSTAL_CODE_REGEXP = /^\d{5}$/;
/**
 * Regular expression that matches valid swedish postal code.
 *
 * this regex matches swedish postal code of format like `169 56`, where
 * - `169 56` or `16956` represent swedish valid postal code
 */
const SWEDISH_POSTAL_CODE_REGEXP = /^[1-9]{1}[0-9]{2}\s?\d{2}$/;
/**
 * Regular expression that matches valid international postal code.
 *
 * this regex matches swedish postal code of format like `169 56`, where
 * - `169 56` or `16956` represent swedish valid postal code
 */
const INTERNATIONAL_POSTAL_CODE_REGEXP = /^[a-zA-Z1-9]([a-zA-Z0-9\ \-]{1,9}$)/;
/**
 * Regular expression that matches valid swedish apartment number.
 *
 * this regex matches swedish postal code of format like `169 56`, where
 * - `169 56` or `16956` represent swedish valid postal code
 */
const SWEDISH_APARTMENT_NUMBER_REGEXP = /^[0-9]{4}$/;
/**
 * Regular expression that matches valid swedish or finnish postal town.
 *
 * this regex matches swedish or finnish postal town of format like `Haapajärvi'.
 *
 */
const SWEDISH_FINNISH_POSTAL_TOWN_REGEXP = /^([a-zA-ZåäöÅÄÖ\ \-_]){1,60}$/;
/**
 * Regular expression that matches valid password according to Business Rule 16.
 * This expression is modified to handle whitespace and omits the final "." to match any character.
 *
 * The password must contain
 * - between 8 and 16 characters
 * - one uppercase letter,
 * - one lowercase letter,
 * - one digit and
 * - one special character.
 * - example 'Abcdefghi1$'
 * - no spaces
 */
const PASSWORD_BR_16 = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z])[a-zA-Z0-9!@#\$%\^&\*]{8,16}$/;
/**
 * Regular expression that matches valid characters for street names.
 *
 * The street name must contain
 *  a limit of 100 characters
 * - between a-z and A-Z characters
 * - can also include special characters like å, ä, ö, Å, Ä, Ö as added below,
 */
const STREET_NAME_PREMISE_REGEXP = /^([a-zA-Z0-9åäöÅÄÖÆæØøÜüßéÉ\ \-\.\,]){1,100}$/;
/**
 * Regular expression that matches valid characters for street names.
 *
 * The street name must contain
 *  a limit of 100 characters
 * - between a-z and A-Z characters
 * - can also include special characters like Å, Ä, Ö, Æ, Ø, Ü, ß as added below,
 */
const STREET_NAME_INVOICE_REGEXP = /^([a-zA-Z0-9åäöÅÄÖÆæØøÜüßéÉ\ \-\.\,]){1,100}$/;
/**
 * Regular expression that matches valid characters for care of address.
 *
 * The care of address must contain
 *  a limit of 150 characters
 * - between a-z and A-Z characters
 * - can also include special characters like Å, Ä, Ö, Æ, Ø, Ü, ß as added below,
 */
const CARE_OF_ADDRESS_REGEX = /^([a-zA-Z0-9åäöÅÄÖÆæØøÜüßéÉ\ \-\(\)]){0,150}$/;
/**
 * Validates a domain extention against a list of official domain registries (filtered by length 2 to 4 characters).
 * @see [IANA Domain Registry](http://data.iana.org/TLD/tlds-alpha-by-domain.txt)
 * @param emailAddress The email address to validate
 */
const hasValidDomainExtention = (emailAddress) => {
    // list of official domain extentions from the IANA domain registry
    // filtered by 2 to 4 characters
    // @ts-ignore
    const IANA_DOMAIN_REGISTRY = [
        "aaa",
        "aarp",
        "abb",
        "abc",
        "able",
        "ac",
        "aco",
        "ad",
        "adac",
        "ads",
        "ae",
        "aeg",
        "aero",
        "af",
        "afl",
        "ag",
        "ai",
        "aig",
        "akdn",
        "al",
        "ally",
        "am",
        "amex",
        "anz",
        "ao",
        "aol",
        "app",
        "aq",
        "ar",
        "arab",
        "army",
        "arpa",
        "art",
        "arte",
        "as",
        "asda",
        "asia",
        "at",
        "au",
        "audi",
        "auto",
        "aw",
        "aws",
        "ax",
        "axa",
        "az",
        "ba",
        "baby",
        "band",
        "bank",
        "bar",
        "bb",
        "bbc",
        "bbt",
        "bbva",
        "bcg",
        "bcn",
        "bd",
        "be",
        "beer",
        "best",
        "bet",
        "bf",
        "bg",
        "bh",
        "bi",
        "bid",
        "bike",
        "bing",
        "bio",
        "biz",
        "bj",
        "blog",
        "blue",
        "bm",
        "bms",
        "bmw",
        "bn",
        "bo",
        "bofa",
        "bom",
        "bond",
        "boo",
        "book",
        "bot",
        "box",
        "br",
        "bs",
        "bt",
        "buy",
        "buzz",
        "bv",
        "bw",
        "by",
        "bz",
        "bzh",
        "ca",
        "cab",
        "cafe",
        "cal",
        "call",
        "cam",
        "camp",
        "car",
        "care",
        "cars",
        "casa",
        "case",
        "cash",
        "cat",
        "cba",
        "cbn",
        "cbre",
        "cbs",
        "cc",
        "cd",
        "ceo",
        "cern",
        "cf",
        "cfa",
        "cfd",
        "cg",
        "ch",
        "chat",
        "ci",
        "citi",
        "city",
        "ck",
        "cl",
        "club",
        "cm",
        "cn",
        "co",
        "com",
        "cool",
        "coop",
        "cpa",
        "cr",
        "crs",
        "csc",
        "cu",
        "cv",
        "cw",
        "cx",
        "cy",
        "cyou",
        "cz",
        "dad",
        "data",
        "date",
        "day",
        "dclk",
        "dds",
        "de",
        "deal",
        "dell",
        "desi",
        "dev",
        "dhl",
        "diet",
        "dish",
        "diy",
        "dj",
        "dk",
        "dm",
        "dnp",
        "do",
        "docs",
        "dog",
        "dot",
        "dtv",
        "duck",
        "dvag",
        "dvr",
        "dz",
        "eat",
        "ec",
        "eco",
        "edu",
        "ee",
        "eg",
        "er",
        "erni",
        "es",
        "esq",
        "et",
        "eu",
        "eus",
        "fage",
        "fail",
        "fan",
        "fans",
        "farm",
        "fast",
        "fi",
        "fiat",
        "fido",
        "film",
        "fire",
        "fish",
        "fit",
        "fj",
        "fk",
        "flir",
        "fly",
        "fm",
        "fo",
        "foo",
        "food",
        "ford",
        "fox",
        "fr",
        "free",
        "frl",
        "ftr",
        "fun",
        "fund",
        "fyi",
        "ga",
        "gal",
        "game",
        "gap",
        "gay",
        "gb",
        "gbiz",
        "gd",
        "gdn",
        "ge",
        "gea",
        "gent",
        "gf",
        "gg",
        "ggee",
        "gh",
        "gi",
        "gift",
        "gl",
        "gle",
        "gm",
        "gmbh",
        "gmo",
        "gmx",
        "gn",
        "gold",
        "golf",
        "goo",
        "goog",
        "gop",
        "got",
        "gov",
        "gp",
        "gq",
        "gr",
        "gs",
        "gt",
        "gu",
        "guge",
        "guru",
        "gw",
        "gy",
        "hair",
        "haus",
        "hbo",
        "hdfc",
        "help",
        "here",
        "hgtv",
        "hiv",
        "hk",
        "hkt",
        "hm",
        "hn",
        "host",
        "hot",
        "how",
        "hr",
        "hsbc",
        "ht",
        "hu",
        "ibm",
        "icbc",
        "ice",
        "icu",
        "id",
        "ie",
        "ieee",
        "ifm",
        "il",
        "im",
        "imdb",
        "immo",
        "in",
        "inc",
        "info",
        "ing",
        "ink",
        "int",
        "io",
        "iq",
        "ir",
        "is",
        "ist",
        "it",
        "itau",
        "itv",
        "java",
        "jcb",
        "je",
        "jeep",
        "jio",
        "jll",
        "jm",
        "jmp",
        "jnj",
        "jo",
        "jobs",
        "jot",
        "joy",
        "jp",
        "jprs",
        "kddi",
        "ke",
        "kfh",
        "kg",
        "kh",
        "ki",
        "kia",
        "kim",
        "kiwi",
        "km",
        "kn",
        "kp",
        "kpmg",
        "kpn",
        "kr",
        "krd",
        "kred",
        "kw",
        "ky",
        "kz",
        "la",
        "land",
        "lat",
        "law",
        "lb",
        "lc",
        "lds",
        "lego",
        "lgbt",
        "li",
        "lidl",
        "life",
        "like",
        "limo",
        "link",
        "live",
        "lk",
        "llc",
        "llp",
        "loan",
        "loft",
        "lol",
        "love",
        "lpl",
        "lr",
        "ls",
        "lt",
        "ltd",
        "ltda",
        "lu",
        "luxe",
        "lv",
        "ly",
        "ma",
        "maif",
        "man",
        "map",
        "mba",
        "mc",
        "md",
        "me",
        "med",
        "meet",
        "meme",
        "men",
        "menu",
        "mg",
        "mh",
        "mil",
        "mini",
        "mint",
        "mit",
        "mk",
        "ml",
        "mlb",
        "mls",
        "mm",
        "mma",
        "mn",
        "mo",
        "mobi",
        "moda",
        "moe",
        "moi",
        "mom",
        "moto",
        "mov",
        "mp",
        "mq",
        "mr",
        "ms",
        "msd",
        "mt",
        "mtn",
        "mtr",
        "mu",
        "mv",
        "mw",
        "mx",
        "my",
        "mz",
        "na",
        "nab",
        "name",
        "navy",
        "nba",
        "nc",
        "ne",
        "nec",
        "net",
        "new",
        "news",
        "next",
        "nf",
        "nfl",
        "ng",
        "ngo",
        "nhk",
        "ni",
        "nico",
        "nike",
        "nl",
        "no",
        "now",
        "np",
        "nr",
        "nra",
        "nrw",
        "ntt",
        "nu",
        "nyc",
        "nz",
        "obi",
        "off",
        "ollo",
        "om",
        "one",
        "ong",
        "onl",
        "ooo",
        "open",
        "org",
        "ott",
        "ovh",
        "pa",
        "page",
        "pars",
        "pay",
        "pccw",
        "pe",
        "pet",
        "pf",
        "pg",
        "ph",
        "phd",
        "pics",
        "pid",
        "pin",
        "ping",
        "pink",
        "pk",
        "pl",
        "play",
        "plus",
        "pm",
        "pn",
        "pnc",
        "pohl",
        "porn",
        "post",
        "pr",
        "pro",
        "prod",
        "prof",
        "pru",
        "ps",
        "pt",
        "pub",
        "pw",
        "pwc",
        "py",
        "qa",
        "qpon",
        "qvc",
        "raid",
        "re",
        "read",
        "red",
        "reit",
        "ren",
        "rent",
        "rest",
        "rich",
        "ril",
        "rio",
        "rip",
        "rmit",
        "ro",
        "room",
        "rs",
        "rsvp",
        "ru",
        "ruhr",
        "run",
        "rw",
        "rwe",
        "sa",
        "safe",
        "sale",
        "sap",
        "sarl",
        "sas",
        "save",
        "saxo",
        "sb",
        "sbi",
        "sbs",
        "sc",
        "sca",
        "scb",
        "scot",
        "sd",
        "se",
        "seat",
        "seek",
        "ses",
        "sew",
        "sex",
        "sexy",
        "sfr",
        "sg",
        "sh",
        "shaw",
        "shia",
        "shop",
        "show",
        "si",
        "silk",
        "sina",
        "site",
        "sj",
        "sk",
        "ski",
        "skin",
        "sky",
        "sl",
        "sm",
        "sn",
        "sncf",
        "so",
        "sohu",
        "song",
        "sony",
        "soy",
        "spa",
        "spot",
        "sr",
        "srl",
        "ss",
        "st",
        "star",
        "stc",
        "su",
        "surf",
        "sv",
        "sx",
        "sy",
        "sz",
        "tab",
        "talk",
        "tax",
        "taxi",
        "tc",
        "tci",
        "td",
        "tdk",
        "team",
        "tech",
        "tel",
        "teva",
        "tf",
        "tg",
        "th",
        "thd",
        "tiaa",
        "tips",
        "tj",
        "tjx",
        "tk",
        "tl",
        "tm",
        "tn",
        "to",
        "top",
        "town",
        "toys",
        "tr",
        "trv",
        "tt",
        "tube",
        "tui",
        "tv",
        "tvs",
        "tw",
        "tz",
        "ua",
        "ubs",
        "ug",
        "uk",
        "uno",
        "uol",
        "ups",
        "us",
        "uy",
        "uz",
        "va",
        "vana",
        "vc",
        "ve",
        "vet",
        "vg",
        "vi",
        "vig",
        "vin",
        "vip",
        "visa",
        "viva",
        "vivo",
        "vn",
        "vote",
        "voto",
        "vu",
        "wang",
        "wed",
        "weir",
        "wf",
        "wien",
        "wiki",
        "win",
        "wine",
        "wme",
        "work",
        "wow",
        "ws",
        "wtc",
        "wtf",
        "xbox",
        "xin",
        "xxx",
        "xyz",
        "ye",
        "yoga",
        "you",
        "yt",
        "yun",
        "za",
        "zara",
        "zero",
        "zip",
        "zm",
        "zone",
        "zw",
    ];
    return pipe(toLower, // parse as lowercase
    split("."), // split by dots
    last, // take the top domain from the end of the list
    includes(__, IANA_DOMAIN_REGISTRY) // check if its in the list of official domain extentions list
    )(emailAddress);
};
/**
 * Validates a Swedish Personal Number or Business Registration Number control character (according to the Luhn algorithm).
 * @see [Luhn algorithm](https://en.m.wikipedia.org/wiki/Luhn_algorithm)
 * @see [Swedish Personal Number](https://en.wikipedia.org/wiki/Personal_identity_number_(Sweden))
 * @param identificationNumber the identification number to validate
 */
const hasValidSwedishChecksum = (identificationNumber) => {
    const doubleEvenNumbers = addIndex(map)((digit, index) => (index % 2 === 0 ? digit * 2 : digit));
    const sumDoubleDigitNumbers = map((digit) => (digit > 9 ? digit - 9 : digit));
    const isDivisibleBy10 = (digit) => digit % 10 === 0;
    return pipe(replace(/\D/g, ""), // Remove non numericals
    takeLast(10), // check when value is positive number, take last 10 characters
    split(""), // Split into array
    map(Number), // Parse characters as numericals
    doubleEvenNumbers, // Double every second digit
    sumDoubleDigitNumbers, // Add double digits together to get single digit numbers only
    sum, // Sum of all digits
    isDivisibleBy10 // Checks that the sum is divisible by 10
    )(identificationNumber);
};
/**
 * Returns birthdate from a Swedish or Finnish personal number as a Date object
 */
const generateDateFromDatePart = (datePart, region, controlCharacter) => {
    switch (region) {
        case "se": {
            const [year, month, day] = datePart;
            //assuming day is valid day
            let validDay = day;
            //check control character '+', if person is greater than 100 year, else fecth current year
            const baseYear = controlCharacter === "+" ? getYear(new Date()) - 100 : getYear(new Date());
            //calculation to get person's born year
            const fullYear = baseYear - ((baseYear - year) % 100);
            //check if days are valid in personal number
            let isValidDays = day > 0 && day <= getDaysInMonth(new Date(fullYear, month - 1));
            //check if its co-ordination number, if days are not valid in previous step
            if (!isValidDays) {
                isValidDays = day > 60 && day <= getDaysInMonth(new Date(fullYear, month - 1)) + 60;
                //if its coordination number subtract 60 in it.
                validDay = isValidDays ? day - 60 : day;
            }
            //return date
            return new Date(fullYear, month - 1, validDay);
        }
        // Create Date object from Finnish personal number
        case "fi": {
            const [day, month, year] = datePart;
            //control character to indicate century mapping eg. "A" equals 2000, "+" equals 1800, "-" equals 1900
            const baseYear = controlCharacter === "A" ? 2000 : controlCharacter === "+" ? 1800 : 1900;
            //add to form person's born year
            const fullYear = baseYear + year;
            //return date
            return new Date(fullYear, month - 1, day);
        }
        default:
            throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'minAge' and 'maxAge'.`);
    }
};
/**
 * This method will assure that a personal number is not created
 * from a future date, for example 20950505-#### can be mistaken for 19950505-####
 * Year and month is used for comparasion due to special treatment for daynumber that
 * sometimes exceeds the calendar days. Example: 870565-####
 * @param personalNumber Swedish or Finnish validated personal number
 * @param region Sweden or Finland is allowed
 */
const isFutureYear = (personalNumber, region) => {
    if (region !== "se" && region !== "fi")
        throwError(`isFutureYear: unknown region: ${region}`);
    const removeLast = (n) => (s) => s.slice(0, -n);
    const replaceChar = (co) => (ce) => (s) => s.replace(co, ce);
    const lengthIs = (n) => (s) => s.length === n;
    const prefix = (p) => (s) => `${p}${s}`;
    const splitToYMDates = (YYYYMMDD) => [YYYYMMDD.slice(0, 4), YYYYMMDD.slice(4, 6), "1"];
    const toDateFromNumberArray = ([Y, monthIndex, D]) => new Date(Y, monthIndex - 1, D);
    const toMsFromDate = (d) => d.getTime();
    const diffTo = (n) => (m) => n - m;
    //yyMMddYY -> YYyyMMdd example June 3rd 1980 03068019 -> 19800603
    const fiToYMD = (s) => `${s[6]}${s[7]}${s[4]}${s[5]}${s[2]}${s[3]}${s[0]}${s[1]}`;
    // YYyymmdd -> true|false
    const isYearMonthFuture = (s) => pipe(splitToYMDates, map((x) => parseInt(x, 10)), toDateFromNumberArray, toMsFromDate, diffTo(Date.now()), (x) => x < 0)(s);
    // sv: [YYyy|yy]mmdd[-|+]#### -> YYyymmdd
    const swedishYearMonthDiff = (s) => pipe(removeLast(4), replaceChar("-")(""), replaceChar("+")(""), ifElse(lengthIs(6), prefix("19"), (x) => x), isYearMonthFuture)(s);
    // fi:ddmmyyYYK#### -> YYyymmdd
    const finnishYearMonthDiff = (s) => pipe(removeLast(4), replaceChar("-")("19"), replaceChar("A")("20"), replaceChar("a")("20"), fiToYMD, isYearMonthFuture)(s);
    return region === "se" ? swedishYearMonthDiff(personalNumber) : finnishYearMonthDiff(personalNumber);
};
/**
 * Validates the personal number has valid date and age is within min and max range.
 * @param personalNumber the personal number to validate
 * @param region indicator to calculate age in yyMMdd or ddMMyy format
 * @param minAge the person's minimum age limit
 * @param maxAge the person's maximum age limit
 */
const hasValidAgeRange = (personalNumber, region, minAge = 0, maxAge = 999) => {
    const getBirthDate = (datePart) => generateDateFromDatePart(datePart, region, personalNumber.charAt(6));
    const getAge = (birthDate) => differenceInYears(new Date(), birthDate);
    const isValidAge = both(lte(__, maxAge), gte(__, minAge));
    const hasAgeWithinLimit = (p) => pipe(replace(/\D/g, ""), // Remove non numericals
    //@ts-ignore
    takeLast(10), // Take last 10 characters
    take(6), // Take first 6 characters
    splitEvery(2), // Split it in array of 2 characters
    map(Number), // Parse characters as numericals
    getBirthDate, // get birth date from personal number
    getAge, // calculate age differences
    isValidAge // Check if age is within limit
    //@ts-ignore
    )(p);
    return hasAgeWithinLimit(personalNumber) && !isFutureYear(personalNumber, region);
};
/**
 * Validates the control character of a Finnish Personal Identity Code.
 * @see [Finnish Personal Identity Code](https://en.wikipedia.org/wiki/National_identification_number#Finland)
 * @param identificationNumber the identification number to validate
 */
const hasValidFinnishPersonalChecksum = (identificationNumber) => {
    const [calculationBase, controlCharacter] = splitAt(-1, identificationNumber);
    const hasMatchingControlCharacter = (checkSum) => equals("0123456789ABCDEFHJKLMNPRSTUVWXY".charAt(checkSum), controlCharacter);
    return pipe(replace(/\D/g, ""), // Remove non numericals
    Number, // Parse as number
    modulo(__, 31), // Divides the calculation base with 31 to get the checksum
    hasMatchingControlCharacter // Checks that control character from check sum match control character from identification number
    )(calculationBase);
};
/**
 * Validates the control character of a Finnish personal number.
 * @see [Finnish Business Identity Code](http://tarkistusmerkit.teppovuori.fi/tarkmerk.htm#y-tunnus2)
 * @param identificationNumber the identification number to validate
 */
const hasValidFinnishBusinessChecksum = (identificationNumber) => {
    const [calculationBase, controlCharacter] = splitAt(-1, identificationNumber);
    return pipe(replace(/\D/gi, ""), // Remove non numericals
    split(""), // Split into array
    map(Number), // Parse characters as numericals
    zipWith(multiply, [7, 9, 10, 5, 8, 4, 2]), // Multiply each digit with a set of coefficients defined by Finnish tax authorities
    sum, // Sum of all digits to get the check sum
    modulo(__, 11), // Divide check sum by 11
    when(gt(__, 1), subtract(11)), // If the checksum is greater than 1 substract 11
    String, // Parse it back to a string to match the type of the control character
    equals(controlCharacter) // Checks that control character from check sum match control character from identification number
    )(calculationBase);
};
/**
 * Validates the control character of a Swedish premise number.
 * @param identificationNumber the identification number to validate
 */
const hasValidSwedishPremiseChecksum = (identificationNumber) => {
    const tripleEvenNumbers = addIndex(map)((digit, index) => (index % 2 ? digit : digit * 3));
    return pipe(split(""), // Split into array
    map(Number), // Parse characters as numericals
    tripleEvenNumbers, // Triple ever even number
    sum, // Sum all numbers
    subtract(1000), // Substract sum from 1000
    modulo(__, 10), // Divide by 10
    equals(0) // Check that the checksum equals 0
    )(identificationNumber);
};
/**
 * Checks that the provided Swedish email address is correctly formated and has an official domain extention.
 * @param swedishEmailAddress the email address to validate
 */
const isValidSwedishEmailAddress = (swedishEmailAddress) => !isEmpty(swedishEmailAddress) &&
    SWEDISH_EMAIL.test(swedishEmailAddress.toLowerCase()) &&
    hasValidDomainExtention(swedishEmailAddress);
/**
 * Checks that the provided Finnish email address is correctly formated and has an official domain extention.
 * @param finnishEmailAddress the email address to validate
 */
const isValidFinnishEmailAddress = (finnishEmailAddress) => !isEmpty(finnishEmailAddress) &&
    FINNISH_EMAIL.test(finnishEmailAddress.toLowerCase()) &&
    hasValidDomainExtention(finnishEmailAddress);
/**
 * Checks that the provided Swedish personal number is correctly formated and match the calculated checksum figure.
 * @param swedishPersonalNumber the personal number to validate
 */
const isValidSwedishPersonalNumber = (swedishPersonalNumber) => !isEmpty(swedishPersonalNumber) &&
    SWEDISH_PERSONAL_NUMBER_REGEXP.test(swedishPersonalNumber) &&
    hasValidSwedishChecksum(swedishPersonalNumber);
/**
 * Checks that the provided personal name only includes accepted characters.
 * @param personalName the personal number to validate
 */
const isValidPersonalName = (personalName) => !isEmpty(personalName) &&
    SWEDISH_PERSONAL_NAME_REGEXP.test(personalName) &&
    !personalName.match(/[\s]{2}/g) &&
    !personalName.match(/[--]{2}/g);
/**
 * Checks that the provided Swedish business number is correctly formated and match the calculated checksum figure.
 * @param swedishBusinessNumber the business number to validate
 */
const isValidSwedishBusinessNumber = (swedishBusinessNumber) => !isEmpty(swedishBusinessNumber) &&
    SWEDISH_ORGANISATION_NUMBER_REGEXP.test(swedishBusinessNumber) &&
    hasValidSwedishChecksum(swedishBusinessNumber);
/**
 * Checks that the provided Finnish personal number is correctly formated and match the calculated checksum figure.
 * @param finnishPersonalNumber the personal number to validate
 */
const isValidFinnishPersonalNumber = (finnishPersonalNumber) => !isEmpty(finnishPersonalNumber) &&
    FINNISH_PERSONAL_NUMBER_REGEXP.test(finnishPersonalNumber) &&
    hasValidFinnishPersonalChecksum(finnishPersonalNumber);
/**
 * Checks that the provided Swedish or Finnish personal number has valid legal age.
 * @param personalNumber the personal number to validate
 * @param region region to validate against different rules
 * @param minAge minimum legal age for personal number to validate against
 * @param maxAge maximum legal age for personal number to validate against
 */
const isValidAgeRange = (personalNumber, region, minAge, maxAge) => !isEmpty(personalNumber) && hasValidAgeRange(personalNumber, region, minAge, maxAge);
/**
 * Checks that the provided Swedish Business Number is correctly formated and match the calculated checksum figure.
 * @param finnishBusinessNumber the business number to validate
 */
const isValidFinnishBusinessNumber = (finnishBusinessNumber) => !isEmpty(finnishBusinessNumber) &&
    FINNISH_ORGANISATION_NUMBER_REGEXP.test(finnishBusinessNumber) &&
    hasValidFinnishBusinessChecksum(finnishBusinessNumber);
/**
 * Checks that the provided Swedish phone number is correctly formated.
 * @param swedishPhoneNumber the phone number to validate
 */
const isValidSwedishPhoneNumber = (swedishPhoneNumber) => !isEmpty(swedishPhoneNumber) && SWEDISH_PHONE_NUMBER_REGEXP.test(swedishPhoneNumber);
/**
 * Checks that the provided Finnish phone number is correctly formated.
 * @param finnishPhoneNumber the phone number to validate
 */
const isValidFinnishPhoneNumber = (finnishPhoneNumber) => !isEmpty(finnishPhoneNumber) && FINNISH_PHONE_NUMBER_REGEXP.test(finnishPhoneNumber);
/**
 * Checks that the provided Swedish postal code is correctly formated.
 * @param swedishPostalCode the postal code to validate
 */
const isValidSwedishPostalCode = (swedishPostalCode) => !isEmpty(swedishPostalCode) && SWEDISH_POSTAL_CODE_REGEXP.test(swedishPostalCode);
/**
 * Checks that the provided International postal code is correctly formated.
 * @param internationalPostalCode the postal code to validate
 */
const isValidInternationalPostalCode = (internationalPostalCode) => !isEmpty(internationalPostalCode) && INTERNATIONAL_POSTAL_CODE_REGEXP.test(internationalPostalCode);
/**
 * Checks that the provided Swedish apartment number is correctly formated.
 * @param swedishApartmentNumber the postal code to validate
 */
const isValidSwedishApartmentNumber = (swedishApartmentNumber) => !isEmpty(swedishApartmentNumber) && SWEDISH_APARTMENT_NUMBER_REGEXP.test(swedishApartmentNumber);
/**
 * Checks that the provided Swedish or Finnish postal town is correctly formatted.
 * @param postalTown the postal town to validate
 */
const isValidSwedishFinnishPostalTown = (postalTown) => !isEmpty(postalTown) && SWEDISH_FINNISH_POSTAL_TOWN_REGEXP.test(postalTown);
/**
 * Checks that the provided Finnish postal code is correctly formated.
 * @param finnishPostalCode the postal code to validate
 */
const isValidFinnishPostalCode = (finnishPostalCode) => !isEmpty(finnishPostalCode) && FINNISH_POSTAL_CODE_REGEXP.test(finnishPostalCode);
/**
 * Checks that the provided Swedish premise identifier is correctly formated.
 * @param swedishPremiseId the premise id to validate
 */
const isValidSwedishPremiseId = (swedishPremiseId) => !isEmpty(swedishPremiseId) &&
    SWEDISH_PREMISE_ID_REGEXP.test(swedishPremiseId) &&
    hasValidSwedishPremiseChecksum(swedishPremiseId);
/**
 * Checks that the provided password is correctly formatted.
 * @param password the password to validate
 */
const isValidPassword = (password) => {
    return PASSWORD_BR_16.test(password);
};
/**
 * Checks that the provided premise street name is correctly formatted
 * for both premise and invoice details
 * @param streetName the street name to validate
 */
const isValidStreetName = (streetName, context) => {
    switch (context) {
        case "premise":
            return STREET_NAME_PREMISE_REGEXP.test(streetName);
        case "invoice":
            return STREET_NAME_INVOICE_REGEXP.test(streetName);
        default:
            throw Error(`[ Vattenfall Elements ] The provided context '${context}' is not supported by the validator streetName.`);
    }
};
/**
 * Checks that the provided care of address is correctly formatted.
 * @param careOfAddress the careOfAddress to validate
 */
const isValidCareOfAddress = (careOfAddress) => {
    return CARE_OF_ADDRESS_REGEX.test(careOfAddress);
};
/**
 * Validates a Grid Area against a list of Grid Area Id found through below source.
 * @see [Grid Area (Whole Sweden)](https://mimer.svk.se/NetworkArea/Search)
 * @param gridAreaId The grid area id to validate
 */
const isValidGridAreaId = (gridAreaId) => {
    // @ts-ignore
    const GRID_AREA_ID = [
        "AJG",
        "ALS",
        "AMS",
        "ALV",
        "ARD",
        "AJB",
        "ARV",
        "AVE",
        "AVR",
        "BEF",
        "BRG",
        "BGS",
        "BGO",
        "BEV",
        "BJR",
        "BKE",
        "BJL",
        "BJK",
        "BLE",
        "BLI",
        "BDN",
        "NBL",
        "BOO",
        "BHM",
        "BRL",
        "BRS",
        "BDL",
        "BMA",
        "BYG",
        "CBR",
        "DAL",
        "DAD",
        "DAP",
        "DAN",
        "DFS",
        "DOF",
        "DRV",
        "EBN",
        "EKO",
        "ESJ",
        "EBA",
        "EKG",
        "ESK",
        "FGA",
        "FBN",
        "FBG",
        "FLN",
        "FLO",
        "FPD",
        "FSB",
        "FLE",
        "FOB",
        "FBA",
        "FRV",
        "FMD",
        "GNF",
        "GAM",
        "GAB",
        "GDB",
        "GIV",
        "GTL",
        "GTP",
        "GBN",
        "GLG",
        "GVL",
        "GTA",
        "GBG",
        "HBO",
        "HLB",
        "HLD",
        "NHA",
        "HVI",
        "HMD",
        "HAM",
        "HMH",
        "HMM",
        "HMR",
        "HRS",
        "HVN",
        "HBY",
        "HDS",
        "HBG",
        "HLJ",
        "HJO",
        "HJM",
        "HOF",
        "HDL",
        "HDG",
        "HDV",
        "HUV",
        "HAL",
        "HAR",
        "HJD",
        "HJN",
        "HSD",
        "HRA",
        "HOG",
        "HOR",
        "ING",
        "JUJ",
        "JMT",
        "JML",
        "JKE",
        "JRA",
        "JPS",
        "JNK",
        "KAB",
        "KMR",
        "KBG",
        "KHN",
        "KSA",
        "KKA",
        "KST",
        "KHB",
        "KEM",
        "KND",
        "KKB",
        "KLK",
        "KPN",
        "KNP",
        "KON",
        "KOB",
        "KNS",
        "KBR",
        "KRF",
        "KRS",
        "KRH",
        "KRG",
        "KBA",
        "KUB",
        "KUV",
        "KVI",
        "KVM",
        "LHM",
        "LKA",
        "LER",
        "LRM",
        "LDG",
        "LDK",
        "LED",
        "LGR",
        "LDB",
        "LNK",
        "LEV",
        "LJV",
        "LBY",
        "LJD",
        "LMA",
        "LUL",
        "LAO",
        "LUN",
        "LYC",
        "LYX",
        "LYS",
        "MBT",
        "MAF",
        "MMO",
        "MLG",
        "MAI",
        "MSD",
        "MEL",
        "VFM",
        "MBY",
        "MOR",
        "MYN",
        "MDL",
        "MRP",
        "NRL",
        "NSF",
        "NOB",
        "NVS",
        "VFN",
        "NTE",
        "NSB",
        "NBR",
        "NYK",
        "NYD",
        "NHN",
        "NFO",
        "NKL",
        "NSO",
        "OBO",
        "OLS",
        "OSD",
        "ORV",
        "ORT",
        "OHN",
        "OXD",
        "PTE",
        "PIB",
        "RSJ",
        "RBY",
        "RSK",
        "RBF",
        "ROY",
        "SAH",
        "SHT",
        "SDV",
        "SIG",
        "SIH",
        "SJO",
        "SHD",
        "SJB",
        "SKA",
        "SKT",
        "SKF",
        "SKR",
        "SKP",
        "SBR",
        "SKL",
        "SNM",
        "SKV",
        "SKH",
        "SKN",
        "SKD",
        "SLD",
        "SMB",
        "SMN",
        "SML",
        "SMS",
        "SOT",
        "SFP",
        "ST1",
        "ST2",
        "ST3",
        "ST4",
        "STF",
        "SGD",
        "STH",
        "STO",
        "ARN",
        "SGS",
        "STU",
        "SRF",
        "SUR",
        "SUV",
        "SBV",
        "SWP",
        "SYM",
        "SYD",
        "SBK",
        "SFE",
        "STR",
        "SSJ",
        "SHN",
        "SDT",
        "SBG",
        "SBL",
        "TIB",
        "THM",
        "TLB",
        "TDN",
        "TNN",
        "TDO",
        "TOR",
        "TOS",
        "TNS",
        "TBG",
        "TRH",
        "TRS",
        "TUM",
        "TYS",
        "TYR",
        "TBY",
        "TRE",
        "TBA",
        "UVA",
        "UHN",
        "UMA",
        "UME",
        "UMD",
        "UMN",
        "UMO",
        "UPN",
        "UPS",
        "UPV",
        "UPO",
        "URM",
        "UPP",
        "VGD",
        "VAG",
        "VAB",
        "VAT",
        "VNB",
        "VAR",
        "VBG",
        "VBN",
        "VBS",
        "VDM",
        "VTL",
        "VGF",
        "VMA",
        "VBY",
        "VDN",
        "VIG",
        "VLB",
        "VMD",
        "VAL",
        "VML",
        "VMO",
        "VRO",
        "VAS",
        "VAD",
        "VBL",
        "VBR",
        "VBK",
        "VVI",
        "VLS",
        "VFV",
        "VAN",
        "VXO",
        "YST",
        "ALL",
        "ADL",
        "AKB",
        "ALM",
        "AML",
        "ANE",
        "ANN",
        "AMN",
        "ANG",
        "ARS",
        "ASL",
        "AVD",
        "ABB",
        "AHB",
        "OCK",
        "OLD",
        "OVK",
        "OFB",
        "OLN",
        "OHM",
        "OGO",
        "VFO",
    ];
    return pipe(toUpper, includes(__, GRID_AREA_ID))(gridAreaId);
};

/*
 * Public API Surface of util
 * Here we expose functions and APIs that are supposed to be used accross projects (ui, util, main project).
 */

/**
 * Generated bundle index. Do not edit.
 */

export { camelToDashCase, coerceBooleanProperty, coerceNumberProperty, evolveAny, isNullable, isNullableObject, isValidAgeRange, isValidCareOfAddress, isValidFinnishBusinessNumber, isValidFinnishEmailAddress, isValidFinnishPersonalNumber, isValidFinnishPhoneNumber, isValidFinnishPostalCode, isValidGridAreaId, isValidInternationalPostalCode, isValidPassword, isValidPersonalName, isValidStreetName, isValidSwedishApartmentNumber, isValidSwedishBusinessNumber, isValidSwedishEmailAddress, isValidSwedishFinnishPostalTown, isValidSwedishPersonalNumber, isValidSwedishPhoneNumber, isValidSwedishPostalCode, isValidSwedishPremiseId, kebabToCamelCase, nextWeekday, nullify, sanitize, toDDMMYYYY, toDate, toEpochSeconds };
//# sourceMappingURL=vattenfall-util.js.map
